import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { getCookiePreferences, setCookiePreferences, type CookiePreferences } from '@/lib/cookie-consent';

interface CookieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CookieSettings({ isOpen, onClose }: CookieSettingsProps) {
  const [preferences, setPreferences] = React.useState<CookiePreferences>(getCookiePreferences());

  const handleSave = () => {
    setCookiePreferences(preferences);
    onClose();
  };

  const handleReset = () => {
    const defaultPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(defaultPreferences);
    setCookiePreferences(defaultPreferences);
    onClose();
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={onClose}
      aria-labelledby="cookie-settings-title"
      aria-describedby="cookie-settings-description"
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle id="cookie-settings-title">Cookie Settings</DialogTitle>
          <DialogDescription id="cookie-settings-description">
            Manage your cookie preferences. You can change these settings at any time.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="necessary" className="text-base">Necessary Cookies</Label>
              <p className="text-sm text-gray-500">
                Required for the website to function properly. Cannot be disabled.
              </p>
            </div>
            <Switch
              id="necessary"
              checked={preferences.necessary}
              disabled
              aria-label="Necessary cookies (always enabled)"
            />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="analytics" className="text-base">Analytics Cookies</Label>
              <p className="text-sm text-gray-500">
                Help us understand how visitors interact with our website.
              </p>
            </div>
            <Switch
              id="analytics"
              checked={preferences.analytics}
              onCheckedChange={(checked) => 
                setPreferences(prev => ({ ...prev, analytics: checked }))
              }
              aria-label="Analytics cookies"
            />
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="marketing" className="text-base">Marketing Cookies</Label>
              <p className="text-sm text-gray-500">
                Used to track visitors across websites to display relevant advertisements.
              </p>
            </div>
            <Switch
              id="marketing"
              checked={preferences.marketing}
              onCheckedChange={(checked) => 
                setPreferences(prev => ({ ...prev, marketing: checked }))
              }
              aria-label="Marketing cookies"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4">
          <Button
            variant="outline"
            onClick={handleReset}
            className="w-full sm:w-auto text-primary hover:text-white hover:bg-primary"
            aria-label="Reset cookie preferences to default settings"
          >
            Reset to Default
          </Button>
          <Button 
            onClick={handleSave}
            className="w-full sm:w-auto text-white hover:text-white"
            aria-label="Save cookie preferences"
          >
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 